import { ref, computed, readonly, watch } from 'vue'
import { 
  isOrderUnpaid, 
  canRetryOrderPayment, 
  logOrderDebugInfo, 
} from '../lib/orderUtils'
import { calculateTokenExpiration } from '../lib/tokenUtils'
import { usePaymentStore } from './usePaymentStore'
import { useOrderTime } from './useOrderTime'
import { convertUtcToMoscowTime } from '@shared/lib'
import type { Order, TokenExpirationInfo } from '@entities/order'

/**
 * Composable для управления состоянием заказа
 */
export function useOrderState() {
  const paymentStore = usePaymentStore()
  const orderTime = useOrderTime()
  
  // Reactive состояния
  const tokenExpiration = ref<TokenExpirationInfo | null>(null)
  
  /**
   * Получает текущий заказ из store с преобразованием времени в московское
   */
  const order = computed(() => {
    const storeOrder = paymentStore.order
    logOrderDebugInfo(storeOrder, 'useOrderState.order computed')
    
    if (!storeOrder) return null
    
    // Преобразуем время в московское, если оно еще не преобразовано
    return {
      ...storeOrder,
      createdAt: storeOrder.createdAt ? convertUtcToMoscowTime(storeOrder.createdAt) : storeOrder.createdAt,
      updatedAt: storeOrder.updatedAt ? convertUtcToMoscowTime(storeOrder.updatedAt) : storeOrder.updatedAt,
      // Добавляем поля с подчеркиванием для совместимости
      created_at: storeOrder.createdAt ? convertUtcToMoscowTime(storeOrder.createdAt) : storeOrder.createdAt,
      updated_at: storeOrder.updatedAt ? convertUtcToMoscowTime(storeOrder.updatedAt) : storeOrder.updatedAt,
      // Добавляем поля с подчеркиванием для totalAmount и других полей
      total_amount: storeOrder.totalAmount || storeOrder.total_amount,
      payment_status: storeOrder.paymentStatus || storeOrder.payment_status,
    }
  })
  
  /**
   * Проверяет, есть ли неоплаченный заказ
   */
  const hasUnpaidOrder = computed(() => {
    return isOrderUnpaid(order.value)
  })
  
  /**
   * Проверяет, можно ли повторить оплату заказа
   */
  const canRetryPayment = computed(() => {
    const isTokenExpired = tokenExpiration.value?.isExpired || false
    return canRetryOrderPayment(order.value, isTokenExpired)
  })
  
  /**
   * Получает время до истечения токена
   */
  const timeUntilExpiration = computed(() => {
    return orderTime.remainingTime.value
  })
  
  /**
   * Форматирует оставшееся время до истечения токена
   */
  const formattedTimeRemaining = computed(() => {
    return orderTime.formattedTimeRemaining.value
  })

  /**
   * Устанавливает заказ в store
   */
  function setOrder(orderData: Order): void {
    paymentStore.setOrder(orderData)
  }

  /**
   * Устанавливает ID заказа в store
   */
  function setOrderId(orderId: number): void {
    paymentStore.setOrderId(orderId)
  }

  /**
   * Устанавливает сумму заказа в store
   */
  function setAmount(amount: number): void {
    paymentStore.setAmount(amount)
  }

  /**
   * Устанавливает URL подтверждения оплаты в store
   */
  function setConfirmationUrl(url: string): void {
    paymentStore.setConfirmationUrl(url)
  }

  /**
   * Устанавливает информацию об истечении токена
   */
  function setTokenExpiration(expiration: TokenExpirationInfo | null): void {
    tokenExpiration.value = expiration
    
    // Устанавливаем remainingTime для реактивности
    if (expiration) {
      orderTime.updateRemainingTime(expiration.remainingTime)
    } else {
      orderTime.updateRemainingTime(0)
    }
  }
  
  /**
   * Обновляет оставшееся время (вызывается таймером)
   */
  function updateRemainingTime(time: number): void {
    orderTime.updateRemainingTime(time)
  }

  /**
   * Сбрасывает состояние заказа
   */
  function resetOrderState(): void {
    tokenExpiration.value = null
    paymentStore.resetPayment()
  }

  /**
   * Получает информацию о заказе для отладки
   */
  function getOrderDebugInfo(): any {
    return {
      hasOrder: !!paymentStore.order,
      orderId: paymentStore.orderId,
      amount: paymentStore.amount,
      itemsCount: paymentStore.order?.items?.length || 0,
      hasUnpaidOrderComputed: hasUnpaidOrder.value,
      canRetryPaymentComputed: canRetryPayment.value,
      tokenExpired: tokenExpiration.value?.isExpired || false,
    }
  }

  // Watch эффект для автоматического запуска таймера
  watch([hasUnpaidOrder, order], ([hasUnpaid, currentOrder]) => {
    // Если есть неоплаченный заказ и есть данные о заказе, инициализируем таймер
    if (hasUnpaid && currentOrder?.created_at) {

      
      // Вычисляем время истечения токена
      const tokenExpirationInfo = calculateTokenExpiration(currentOrder.created_at)
      setTokenExpiration(tokenExpirationInfo)
    }
  }, { immediate: true })

  return {
    // State
    order: readonly(order),
    hasUnpaidOrder: readonly(hasUnpaidOrder),
    canRetryPayment: readonly(canRetryPayment),
    timeUntilExpiration: readonly(timeUntilExpiration),
    formattedTimeRemaining: readonly(formattedTimeRemaining),
    tokenExpiration: readonly(tokenExpiration),
    
    // Actions
    setOrder,
    setOrderId,
    setAmount,
    setConfirmationUrl,
    setTokenExpiration,
    updateRemainingTime,
    resetOrderState,
    getOrderDebugInfo,
  }
}